import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ModalBase from "../../../../components/ui/ModalBase";
import { useToastContext } from "../../../../context/ToastContextProvider";
import colors from "../../../../shared/constants/color";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import { isCorporateCommunicationParameterId } from "../../../../constants/communicationParameterGuids";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  getCountryList,
  getCityList,
  getTownListByCity,
} from "../../../../redux/actions/memberAction";
import {
  getAddressDetail,
  updateAddressInfo,
} from "../../../../api/addressApi";

export interface AddressInfoListDTO {
  addressLine: string;
  cityName: string;
  countryID: number;
  cityID: number;
  townID: number;
  communicationParameterID: number;
  postcode: string;
  enteringMemberID: number;
}

interface AddAddressModalProps {
  open: boolean;
  memberId: string | number;
  addressId?: string | null;
  onClose: () => void;
  onSuccess?: (item: AddressInfoListDTO) => void;
}

const initialFormState = {
  address: "",
  addressType: "",
  postalCode: "",
  country: "TÜRKİYE",
  city: "",
  district: "",
};

const AddAddressModal: React.FC<AddAddressModalProps> = ({
  open,
  memberId,
  addressId = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!addressId;
  const { showToast } = useToastContext();
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<typeof initialFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressTypeOptions, setAddressTypeOptions] = useState<any[]>([]);
  const countryList = useAppSelector(
    (state: any) => state.countryList?.countryList || [],
  );
  const cityList = useAppSelector(
    (state: any) => state.cityList?.cityList || [],
  );
  const townList = useAppSelector(
    (state: any) => state.townList?.townList || [],
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pendingDistrictId, setPendingDistrictId] = useState<string | null>(
    null,
  );
  const [pendingCountryId, setPendingCountryId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isTurkey = form.country === "TÜRKİYE" || form.country === "";

  const resetState = () => {
    setForm(initialFormState);
    setErrors({});
    setIsInitialLoad(true);
    setPendingDistrictId(null);
    setPendingCountryId(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      // Modal kapandığında state'leri temizle
      resetState();
      return;
    }

    // Modal her açıldığında isInitialLoad'ı true yap
    setIsInitialLoad(true);

    const fetchData = async () => {
      dispatch(getCountryList());
      dispatch(getCityList());

      try {
        const token = localStorage.getItem("token");
        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined;

        // Adres tiplerini yükle
        const response = await axios.get(
          `${SERVICEURL}api/Data/GetCommunicationParameterList`,
          { headers },
        );

        const addressTypeData = response.data?.data || [];

        const allTypes = Array.isArray(addressTypeData) ? addressTypeData : [];
        // ba2026f: adres tipi listesi artik ad bazli degil, CommunicationParameter.IsAdress
        // bayragi uzerinden geliyor.
        // isCorporateCommunicationParameterId ek kosulu Wiser-Saas'a OZEL: uretimde
        // "Kurumsal" (legacy RDC, id 13) IsAdress = false ve upstream'in duz
        // `.filter(t => t.isAdress)` hali bu kaydi listeden dusuruyor. Wiser-Saas RDC'yi
        // tenant-notr "Kurumsal" iletisim tipi olarak urunlestirdi
        // (src/constants/communicationParameterGuids.ts), secilebilir kalmasi gerekiyor.
        // Seed'le duzeltmek ETL tarafindan geri alinir -- bkz. 011_...ContactFlags.sql not 2.
        const addressTypes = allTypes.filter(
          (type: any) =>
            type.isAdress || isCorporateCommunicationParameterId(type.id),
        );

        setAddressTypeOptions(addressTypes);

        // Düzenleme modundaysa adres verisini yükle
        if (addressId) {
          const data = await getAddressDetail(addressId);

          // Şehir ID'sine göre ilçeleri yükle
          if (data.cityID) {
            await dispatch(getTownListByCity(data.cityID));

            // İlçeler yüklendikten sonra kısa bir bekleme
            await new Promise((resolve) => setTimeout(resolve, 300));
          }

          // İlk yükleme flag'ini false yap ki useEffect ilçeyi sıfırlamasın
          setIsInitialLoad(false);

          // İlçe ID'sini pending olarak sakla - townList güncellendiğinde set edilecek
          if (data.townID) {
            setPendingDistrictId(data.townID.toString());
          }

          // Küçük bir bekleme sonra formu set et
          await new Promise((resolve) => setTimeout(resolve, 50));

          // countryID'yi sakla - countryList yüklendiğinde ülke adı bulunacak
          if (data.countryID) {
            setPendingCountryId(String(data.countryID));
          }

          // countryList'ten ülke adını bul (eğer yüklendiyse)
          let countryName = "TÜRKİYE"; // default
          if (data.countryID && countryList.length > 0) {
            const foundCountry = countryList.find(
              (c: any) => c.id === data.countryID,
            );
            if (foundCountry) {
              countryName = foundCountry.name;
            }
          }

          setForm({
            address: data.addressLine || "",
            addressType: data.communicationParameterID?.toString() || "",
            postalCode: data.postcode || "",
            country: countryName,
            city: data.cityID?.toString() || "",
            district: data.townID?.toString() || "",
          });

          // ba2026f: eski bir kaydin adres tipi (or. IsAdress'i sonradan kapatilmis bir
          // parametre) filtrelenmis listede olmayabilir. O durumda Select bos gorunur ve
          // kaydetme o alani sessizce degistirir; bu yuzden mevcut tip listeye elle eklenir.
          // Upstream Number(...) ile karsilastiriyor; bizde kimlik Guid string oldugu icin
          // Number() -> NaN olur ve kosul asla tutmaz (playbook tuzak 1).
          const currentTypeId = data.communicationParameterID
            ? String(data.communicationParameterID).toLowerCase()
            : "";
          if (
            currentTypeId &&
            !addressTypes.some(
              (type: any) => String(type.id).toLowerCase() === currentTypeId,
            )
          ) {
            const currentType = allTypes.find(
              (type: any) => String(type.id).toLowerCase() === currentTypeId,
            );
            if (currentType) {
              setAddressTypeOptions([...addressTypes, currentType]);
            }
          }

          const foundTown = townList.find((t: any) => t.id === data.townID);
          console.log("Found town:", foundTown);
        } else {
          setForm(initialFormState);
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error("Data loading error:", error);
        if (addressId) {
          showToast("Adres bilgisi yüklenirken bir hata oluştu.", "error");
        }
      }
    };

    fetchData();
  }, [open, addressId, dispatch]);

  // countryList yüklendiğinde pendingCountryId varsa ülke adını bul
  useEffect(() => {
    if (pendingCountryId && countryList.length > 0) {
      const foundCountry = countryList.find(
        (c: any) => c.id === pendingCountryId,
      );
      if (foundCountry && form.country !== foundCountry.name) {
        setForm((prev) => ({ ...prev, country: foundCountry.name }));
      }
      setPendingCountryId(null); // Bir kere bulunduktan sonra temizle
    }
  }, [countryList, pendingCountryId]);

  useEffect(() => {
    if (!isTurkey) {
      setForm((prev) => ({ ...prev, city: "", district: "" }));
      dispatch(getTownListByCity(""));
    }
  }, [form.country, isTurkey, dispatch]);

  useEffect(() => {
    if (isTurkey && form.city && !isInitialLoad) {
      if (form.city) {
        dispatch(getTownListByCity(String(form.city)));
        setForm((prev) => ({ ...prev, district: "" }));
      }
    } else if (isTurkey && !form.city) {
      dispatch(getTownListByCity(""));
    }
  }, [form.city, dispatch, isTurkey, isInitialLoad]);

  // townList güncellendiğinde pending district'i set et
  useEffect(() => {
    if (pendingDistrictId && townList.length > 0) {
      const foundTown = townList.find(
        (t: any) => t.id.toString() === pendingDistrictId,
      );
      console.log(
        "Setting pending district:",
        pendingDistrictId,
        "Found:",
        foundTown,
      );
      if (foundTown) {
        setForm((prev) => ({ ...prev, district: pendingDistrictId }));
        setPendingDistrictId(null); // Sadece bir kez set et
      }
    }
  }, [townList, pendingDistrictId]);

  const validateField = (name: string, value: string) => {
    setErrors((prevErrors) => {
      const newErrors: Record<string, string> = { ...prevErrors };

      if (name === "address") {
        // ce90f95: adres artik zorunlu degil; bos gecilebilir. Dolu ise uzunluk kurali suruyor.
        if (value.trim() && value.trim().length < 3) {
          newErrors.address =
            "Adres bilgisi en az 3, en fazla 100 karakterden oluşabilir";
        } else {
          delete newErrors.address;
        }
      }

      return newErrors;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "postalCode") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setForm((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "address") {
      validateField(name, value);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // ce90f95: adres ve ilce zorunlulugu kaldirildi. Adres tipi, ulke ve sehir zorunlu kaldi.
    if (form.address.trim() && form.address.trim().length < 3) {
      newErrors.address =
        "Adres bilgisi en az 3, en fazla 100 karakterden oluşabilir";
    }

    if (!form.addressType) {
      newErrors.addressType = "Adres tipi seçimi zorunludur.";
    }

    if (!form.country) {
      newErrors.country = "Ülke seçimi zorunludur.";
    }

    if (!form.city) {
      newErrors.city = "Şehir seçimi zorunludur.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);

    try {
      setIsSubmitting(true);

      if (isEditMode && addressId) {
        // Düzenleme modu - PUT isteği
        const selectedCountry = countryList.find(
          (country: any) => country.name === form.country,
        );

        // ⚠️ TUZAK 1 -- CANLI VERI KAYBI DUZELTMESI (upstream'de bu hata YOK, onlarda id'ler int).
        // form.city / form.district Guid string. Number(<Guid>) -> NaN, `NaN || null` -> null
        // oldugu icin bu blok sehir ve ilceyi HIC GONDERMIYORDU; duzenleme kaydedilince
        // AddressInfo.CityID / TownID NULL'a cekiliyordu. cityList.find(id === NaN) de asla
        // eslesmedigi icin cityName daima "" kaliyordu.
        // ce90f95 ilce zorunlulugunu kaldirdigi icin bu hata testte GORUNMEZ hale gelecekti:
        // "ilcesiz kayit" testi gecerken ilceli kayit da ilcesiz yazilirdi.
        // Karar: Dalga 22'de birlikte duzeltildi -- docs/repo-migration/NOT_PORTED.md 5.2
        let selectedCityId: string | null = null;
        let cityName: string | null = null;
        if (isTurkey) {
          selectedCityId = form.city || null;
          const selectedCity = cityList.find(
            (city: any) => String(city.id) === String(form.city),
          );
          cityName = selectedCity?.name || null;
        } else {
          // Yurt disi adreslerde sehir serbest metin; eskiden hic doldurulmuyordu.
          cityName = form.city.trim() || null;
        }

        let selectedTownId: string | null = null;
        if (isTurkey && form.district) {
          selectedTownId = form.district;
        }

        const updatePayload = {
          addressID: addressId,
          // ce90f95: adres bossa acikca null -- BE UpdateAddressInfoRequestDTO.AddressLine
          // artik string? (Adim 4).
          addressLine: form.address.trim() || null,
          cityName: cityName,
          countryID: selectedCountry?.id ? String(selectedCountry.id) : undefined,
          cityID: selectedCityId ?? undefined,
          // ce90f95: ilce bossa NULL gonderilip kolon temizlenir. Onceki `? ... : undefined`
          // hali anahtari hic gondermedigi icin "ilceyi kaldir" niyeti ifade edilemiyordu.
          townID: selectedTownId,
          communicationParameterID: form.addressType || undefined,
          postcode: form.postalCode || "",
        };

        console.log("Update address payload:", updatePayload);
        await updateAddressInfo(updatePayload);
        showToast("Adres bilgisi başarıyla güncellendi.", "success");
        if (onSuccess) {
          onSuccess({} as AddressInfoListDTO);
        }
        handleClose();
      } else {
        // Ekleme modu - POST isteği
        const token = localStorage.getItem("token");
        const currentUserId = localStorage.getItem("memberId");

        // TUZAK 1: kimlikler Guid string; Number(<Guid>) -> NaN, karsilastirma daima false.
        const selectedAddressType = addressTypeOptions.find(
          (type: any) => String(type.id) === String(form.addressType),
        );

        const selectedCountry = countryList.find(
          (country: any) => country.name === form.country,
        );

        let selectedCityId: string | null = null;
        let selectedCityName: string | null = null;
        if (isTurkey) {
          const selectedCity = cityList.find(
            (city: any) => String(city.id) === String(form.city),
          );
          selectedCityId = selectedCity?.id ? String(selectedCity.id) : null;
          selectedCityName = selectedCity?.name || null;
        }

        let selectedTownId: string | null = null;
        if (isTurkey && form.district) {
          const selectedTown = townList.find(
            (town: any) => String(town.id) === String(form.district),
          );
          selectedTownId = selectedTown?.id ? String(selectedTown.id) : null;
        }

        const payload = {
          AddressLine: form.address.trim() || null,
          CityName: isTurkey ? selectedCityName : form.city.trim() || null,
          CountryID: selectedCountry?.id || null,
          CityID: selectedCityId,
          TownID: selectedTownId,
          CommunicationParameterID: form.addressType || null,
          Postcode: form.postalCode || "",
          EnteringMemberID: null,
        };

        const response = await axios.post(
          `${SERVICEURL}api/Candidates/AddAddressInfo/${memberId}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const responseData = response.data?.data || response.data;

        const apiData: AddressInfoListDTO = {
          addressLine:
            responseData?.addressLine || responseData?.AddressLine || "",
          cityName: responseData?.cityName || responseData?.CityName || "",
          countryID: responseData?.countryID || responseData?.CountryID || 0,
          cityID: responseData?.cityID || responseData?.CityID || 0,
          townID: responseData?.townID || responseData?.TownID || 0,
          communicationParameterID:
            responseData?.communicationParameterID ||
            responseData?.CommunicationParameterID ||
            0,
          postcode: responseData?.postcode || responseData?.Postcode || "",
          enteringMemberID:
            responseData?.enteringMemberID ||
            responseData?.EnteringMemberID ||
            0,
        };

        if (onSuccess) {
          onSuccess(apiData);
        }

        showToast("Adres bilgisi başarıyla eklendi.", "success");
        handleClose();
      }
    } catch (error: any) {
      console.error("Address save error:", error);
      const errorMessage = isEditMode
        ? error.response?.data?.message ||
          "Adres bilgisi güncellenirken bir hata oluştu."
        : error.response?.data?.message ||
          "Adres bilgisi eklenirken bir hata oluştu.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModalBase
        open={open}
        onClose={handleClose}
        title={isEditMode ? "Adres Düzenle" : "Yeni Adres Ekle"}
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: 2, minWidth: 80 }}
              disabled={isSubmitting}
              onClick={handleSaveClick}
            >
              {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </>
        }
        maxWidth="md"
        minWidth={800}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          {/* Adres - Full Width */}
          <TextField
            fullWidth
            label="Adres"
            name="address"
            value={form.address}
            onChange={handleChange}
            error={!!errors.address}
            helperText={errors.address}
            size="small"
            multiline
            minRows={3}
            inputProps={{
              maxLength: 100,
            }}
            InputProps={{
              endAdornment:
                form.address !== "" ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: colors.neutral.N1200 },
                      }}
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          address: "",
                        }));
                        // ce90f95: alan artik zorunlu degil -- temizleyince kalan uzunluk
                        // hatasi da dussun, aksi halde bos alanda hata mesaji takili kaliyor.
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.address;
                          return next;
                        });
                      }}
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />

          {/* İkinci satır: Adres Tipi, Posta Kodu */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              select
              fullWidth
              label="Adres Tipi"
              name="addressType"
              value={form.addressType}
              onChange={handleChange}
              size="small"
              error={!!errors.addressType}
              helperText={errors.addressType}
              required
            >
              <MenuItem value="">Seçiniz</MenuItem>
              {addressTypeOptions.map((type) => (
                <MenuItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Posta Kodu"
              name="postalCode"
              value={form.postalCode}
              onChange={handleChange}
              size="small"
              error={!!errors.postalCode}
              helperText={errors.postalCode}
              inputProps={{
                maxLength: 10,
                pattern: "[0-9]*",
              }}
              InputProps={{
                endAdornment:
                  form.postalCode !== "" ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: colors.neutral.N1200 },
                        }}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            postalCode: "",
                          }))
                        }
                      >
                        ✕
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </Box>

          {/* Üçüncü satır: Ülke, Şehir, İlçe */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isTurkey ? "1fr 1fr 1fr" : "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              select
              fullWidth
              label="Ülke"
              name="country"
              value={form.country}
              onChange={handleChange}
              size="small"
              error={!!errors.country}
              helperText={errors.country}
              required
            >
              <MenuItem value="">Seçiniz</MenuItem>
              {countryList.map((country: any) => (
                <MenuItem key={country.id} value={country.name}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>

            {isTurkey ? (
              <TextField
                select
                fullWidth
                label="Şehir"
                name="city"
                value={form.city}
                onChange={handleChange}
                size="small"
                error={!!errors.city}
                helperText={errors.city}
                required
              >
                <MenuItem value="">Seçiniz</MenuItem>
                {cityList.map((city: any) => (
                  <MenuItem key={city.id} value={city.id.toString()}>
                    {city.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label="Şehir"
                name="city"
                value={form.city}
                onChange={handleChange}
                size="small"
                error={!!errors.city}
                helperText={errors.city}
                required
                inputProps={{
                  maxLength: 50,
                }}
                InputProps={{
                  endAdornment:
                    form.city !== "" ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            "&:hover": {
                              backgroundColor: colors.neutral.N1200,
                            },
                          }}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              city: "",
                            }))
                          }
                        >
                          ✕
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
            )}

            {/* İlçe sadece Türkiye seçiliyse göster */}
            {isTurkey && (
              <TextField
                select
                fullWidth
                label="İlçe"
                name="district"
                value={form.district}
                onChange={handleChange}
                size="small"
                disabled={!form.city}
                error={!!errors.district}
                helperText={errors.district}
              >
                <MenuItem value="">Seçiniz</MenuItem>
                {townList.map((town: any) => (
                  <MenuItem key={town.id} value={town.id.toString()}>
                    {town.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </Box>
      </ModalBase>
      <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      disableScrollLock
    >
        <DialogTitle>
          {isEditMode ? "Adres Güncellensin mi?" : "Adres Eklensin mi?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isEditMode
              ? "Adres güncellemek istediğinize emin misiniz?"
              : "Adres eklemek istediğinize emin misiniz?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Hayır
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ textTransform: "none", background: colors.blue.B800 }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAddressModal;
